import React, { useMemo, useState } from "react";
import { Pie } from "react-chartjs-2";
import { Chart, ArcElement, Tooltip, Legend } from "chart.js";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { FiSearch } from "react-icons/fi";
import Card from "components/card";
import { CHART_COLORS } from "../../default/components/FleetSummaryPieChart";

Chart.register(ArcElement, Tooltip, Legend);

const columnHelper = createColumnHelper();

function displayValue(value, column) {
  if (value == null || value === "") return "—";
  if (column?.format === "battery") {
    const number = Number(value);
    if (!Number.isFinite(number)) return String(value);
    return `${Number.isInteger(number) ? number : number.toFixed(1)}%`;
  }
  const text = String(value);
  return column?.suffix ? `${text}${column.suffix}` : text;
}

function headerLabel(label) {
  return function Header({ column }) {
    const sorted = column.getIsSorted();
    return (
      <span className="inline-flex items-center gap-1 text-sm font-bold text-gray-600 dark:text-white">
        {label}
        <span className="inline-flex flex-col text-[9px] leading-none">
          <span className={sorted === "asc" ? "text-brand-500" : "text-gray-300"}>▲</span>
          <span className={sorted === "desc" ? "text-brand-500" : "text-gray-300"}>▼</span>
        </span>
      </span>
    );
  };
}

function ReportDataTable({ title, subtitle, columnDefs, rows, searchKey, emptyText }) {
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const columns = useMemo(
    () => (columnDefs || []).map((column) =>
      columnHelper.accessor(column.key, {
        header: headerLabel(column.label || column.key),
        cell: (info) => (
          <p className={`flex items-center text-sm font-bold text-navy-700 dark:text-white ${
            column.key === (columnDefs[0] && columnDefs[0].key) ? "" : "justify-center"
          }`}>
            {column.swatch ? (
              <span
                className="mr-2 inline-block h-2.5 w-2.5 rounded-full"
                style={{ backgroundColor: info.row.original[column.swatch] || "#A3AED0" }}
              />
            ) : null}
            {displayValue(info.getValue(), column)}
          </p>
        ),
      })
    ),
    [columnDefs]
  );

  const table = useReactTable({
    data: rows || [],
    columns,
    state: { sorting, pagination, columnFilters },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  const leadKey = columnDefs?.[0]?.key;

  return (
    <Card extra="w-full h-full p-6">
      <header className="relative flex items-center justify-between">
        <div>
          <div className="text-xl font-bold text-navy-700 dark:text-white">{title}</div>
          {subtitle ? (
            <p className="mt-1 text-sm font-medium text-gray-600 dark:text-gray-300">{subtitle}</p>
          ) : null}
        </div>
        {searchKey ? (
          <div className="mb-6 flex items-center justify-center">
            <div className="mr-3 flex h-full items-center rounded-full bg-lightPrimary py-2 text-navy-700 dark:bg-navy-900 dark:text-white">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-gray-400 dark:text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block h-full w-full rounded-full bg-lightPrimary text-sm font-medium text-navy-700 outline-none placeholder:!text-gray-400 dark:bg-navy-900 dark:text-white dark:placeholder:!text-white sm:w-fit"
                onChange={(event) => setColumnFilters([{ id: searchKey, value: event.target.value }])}
              />
            </div>
          </div>
        ) : null}
      </header>
      <div className={`mt-8 ${columns.length > 5 ? "overflow-x-auto" : "overflow-x-hidden"}`}>
        <table className="w-full min-w-max">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="!border-px !border-gray-400">
                {headerGroup.headers.map((header) => {
                  const centered = header.column.id !== leadKey;
                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={`border-b-[1px] border-gray-200 pb-2 pt-4 ${
                        centered ? "px-2 text-center" : "pr-4 text-start"
                      }`}
                    >
                      <div className={`flex items-center text-xs text-gray-200 ${centered ? "justify-center" : "justify-start"}`}>
                        <button type="button" onClick={header.column.getToggleSortingHandler()} className="inline-flex cursor-pointer items-center">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </button>
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={Math.max(columns.length, 1)} className="py-6 text-center text-sm font-bold text-navy-700 dark:text-white">
                  {emptyText || "No rows."}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`min-w-[150px] border-white/0 py-3 ${
                        cell.column.id === leadKey ? "pr-4 text-start" : "px-2 text-center"
                      }`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="relative mb-4 flex items-center justify-between pt-4 text-gray-600">
          <div>
            Showing {table.getRowModel().rows.length.toLocaleString()} of {table.getRowCount().toLocaleString()} Rows
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="rounded border p-1" onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage()}>{"<<"}</button>
            <button type="button" className="rounded border p-1" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>{"<"}</button>
            <span className="flex items-center gap-1">
              <div>Page</div>
              <strong>
                {table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
              </strong>
            </span>
            <button type="button" className="rounded border p-1" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>{">"}</button>
            <button type="button" className="rounded border p-1" onClick={() => table.lastPage()} disabled={!table.getCanNextPage()}>{">>"}</button>
          </div>
        </div>
      </div>
    </Card>
  );
}

function ReportPie({ chart }) {
  const slices = chart.slices || [];
  const details = new Map(slices.map((slice) => [slice.label, slice.detail || ""]));
  return (
    <Card extra="rounded-[20px] p-3 h-[450px] relative">
      <div className="flex flex-row justify-between px-3 pt-2">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">{chart.title}</h4>
      </div>
      <div className="mb-auto flex h-[380px] w-full items-center justify-center" style={{ maxHeight: "300px", marginTop: "20px" }}>
        <Pie
          data={{
            labels: slices.map((slice) => slice.label),
            datasets: [{
              data: slices.map((slice) => slice.value),
              backgroundColor: slices.map((slice, index) => slice.color || CHART_COLORS[index % CHART_COLORS.length]),
              borderWidth: 1,
            }],
          }}
          options={{
            responsive: true,
            plugins: {
              legend: { display: true, position: "bottom" },
              tooltip: {
                callbacks: {
                  afterLabel: (item) => details.get(item.label) || "",
                },
              },
            },
          }}
        />
      </div>
    </Card>
  );
}

function ReportView({ view }) {
  const charts = view?.charts || [];
  const tables = view?.tables || [];
  return (
    <div className="mt-5 grid gap-5">
      {view?.summary ? (
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{view.summary}</p>
      ) : null}
      {charts.length > 0 ? (
        <div className="grid gap-5 md:grid-cols-2">
          {charts.filter((chart) => chart.type === "pie" || !chart.type).map((chart, index) => (
            <ReportPie key={`${chart.title || "chart"}-${index}`} chart={chart} />
          ))}
        </div>
      ) : null}
      {tables.map((table, index) => (
        <ReportDataTable
          key={`${table.title || "table"}-${index}`}
          title={table.title}
          subtitle={table.subtitle}
          columnDefs={table.columns || []}
          rows={table.rows || []}
          searchKey={table.searchKey}
          emptyText={table.emptyText}
        />
      ))}
    </div>
  );
}

export default ReportView;
